import styled from "@emotion/styled";
import { FC } from "react";
import { useSelector } from "react-redux";

import { Button, DesignToken, PromoBannerV2, SpacingToken, Text } from "@livechat/design-system-react-components";

import { newFeaturesCampaigns } from "constants/campaigns";
import { HelpCenter } from "constants/help-center";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { withVar } from "themes/helpers";

interface IProps {
  route: TicketsRoutes;
}

export const UndeliveredBanner: FC<IProps> = ({ route }) => {
  const userRole = useSelector(selectLoggedInAgentPermission);
  const isUndeliveredRoute = route === TicketsRoutes.Undelivered;

  const { featureFlagValue: hasClosedBanner, setAgentFlagCallback: setHasClosedBanner } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasClosedUndeliveredViewBanner.flag,
    newFeaturesCampaigns.hasClosedUndeliveredViewBanner.campaignStartDate
  );

  const handleLearnMoreClick = (): void => {
    trackEngagement("Undelivered banner: Learn more clicked", EventPlace.Tickets);
  };

  const handleBannerCloseClick = (): void => {
    trackEngagement("Undelivered banner: Close clicked", EventPlace.Tickets, {
      role: userRole,
      place: "undelivered",
      status: "hide clicked",
    });
    setHasClosedBanner();
  };

  const shouldShowBanner = isUndeliveredRoute && !hasClosedBanner;

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Banner onClose={handleBannerCloseClick}>
      <Text bold>Track undelivered messages</Text>
      <Text>
        See tickets with failed email deliveries. Review and resend as needed.&nbsp;
        <Button
          onClick={handleLearnMoreClick}
          href={HelpCenter.EMAIL_DELIVERY_TRACKING}
          target="_blank"
          kind="link-light"
        >
          Learn more
        </Button>
      </Text>
    </Banner>
  );
};

const Banner = styled(PromoBannerV2)`
  margin: ${withVar(SpacingToken.Spacing5)};
  background: ${withVar(DesignToken.SurfaceGradient07)};
`;
